/**
 * ScnSection 共用產品 SCN (S1.4, extended S7.3) — the OPTIONAL retroactive
 * shared-product link group. Never set on create; Link/Join/Unlink are
 * retroactive PATCHes and past documents ALWAYS keep their references
 * (round-trip safe).
 *
 * S7.3 conditional-requiredness surfacing (required-iff-共用, S0.1/FD6):
 * - shared + UNLINKED: the registry's `getAttentionRules` reports the unmet
 *   `requiredIfShared` demand and the section renders an ATTENTION callout —
 *   affordance-not-status: the callout CARRIES the Link CTA (and the empty
 *   state below drops its duplicate CTA — one affordance). SCN still NEVER
 *   gates 產品就緒 (escalation = Q-O2).
 * - shared + linked / unshared: no callout (the S0.1 state matrix).
 * - linked + Spec Master fields unauthored: a spec-completeness HINT linking
 *   to the group's Spec Master detail (progressive enhancement — loading or
 *   fetch-error renders nothing, never a blocker).
 */

import { Alert, Button, Table, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { DisconnectOutlined, LinkOutlined, RightOutlined, SwapOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState } from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import StatusTag from '@/components/common/StatusTag';
import { getAttentionRules } from '@/config/productFieldRegistry';
import { isScnSpecIncomplete, useScnGroupSpec } from '@/hooks/useProducts';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';

const { Text } = Typography;

interface ScnSectionProps {
  product: Product;
  /** Other products in the same SCN group (self excluded). */
  members: Product[];
  membersLoading: boolean;
  onLink: () => void;
  onUnlink: () => void;
}

export const ScnSection = ({
  product,
  members,
  membersLoading,
  onLink,
  onUnlink,
}: ScnSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();

  // Registry-as-data: the S0.1 attention state comes from the field
  // registry's required-iff-共用 mechanism, never re-derived inline.
  const hasScnAttention = getAttentionRules(product).some((rule) => rule.key === 'scn');

  // Spec-completeness hint feed (linked products only — `ready`-gated).
  const { group, isLoading: specLoading, error: specError } = useScnGroupSpec(product.scn);
  const showSpecHint =
    !!product.scn && !specLoading && !specError && isScnSpecIncomplete(group);

  if (!product.scn) {
    return (
      <div>
        <SectionHeader title={t('products:detail.sections.scn')} subtitle={t('products:scnHelp')} />
        {hasScnAttention && (
          <Alert
            className="tw-mb-4"
            type="warning"
            showIcon
            message={t('products:detail.scn.attention.title')}
            description={t('products:detail.scn.attention.body')}
            action={
              product.isActive ? (
                <Button type="primary" icon={<LinkOutlined />} onClick={onLink}>
                  {t('products:detail.scn.linkAction')}
                </Button>
              ) : undefined
            }
          />
        )}
        <TableContainer>
          <ListEmptyState
            icon={<LinkOutlined />}
            title={t('products:detail.scn.notLinked')}
            description={t('products:detail.scn.optionalNote')}
            // One affordance: the attention callout carries the Link CTA;
            // only the unshared branch keeps it on the empty state.
            actionLabel={
              product.isActive && !hasScnAttention
                ? t('products:detail.scn.linkAction')
                : undefined
            }
            onAction={product.isActive && !hasScnAttention ? onLink : undefined}
            actionIcon={<LinkOutlined />}
          />
        </TableContainer>
      </div>
    );
  }

  const memberColumns: TableProps<Product>['columns'] = [
    {
      title: t('products:fields.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 200,
      render: (sku: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{sku}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.internalProductId}
          </span>
        </div>
      ),
    },
    {
      title: t('products:fields.displayName'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (value: string) => value,
    },
    {
      title: t('common:status.title'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean) => (
        <StatusTag
          status={isActive ? 'active' : 'inactive'}
          label={t(isActive ? 'common:status.active' : 'common:status.inactive')}
        />
      ),
    },
  ];

  return (
    <div>
      <SectionHeader
        title={t('products:detail.sections.scn')}
        subtitle={t('products:detail.scn.retroNote')}
        extra={
          <>
            <Button icon={<SwapOutlined />} disabled={!product.isActive} onClick={onLink}>
              {t('products:detail.scn.changeGroup')}
            </Button>
            <Button
              danger
              icon={<DisconnectOutlined />}
              disabled={!product.isActive}
              onClick={onUnlink}
            >
              {t('products:detail.scn.unlink')}
            </Button>
          </>
        }
      />

      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t('products:fields.scn')}
        </Text>
        <PurpleTag style={{ fontSize: token.fontSize, padding: '2px 12px' }}>
          {product.scn}
        </PurpleTag>
      </div>

      {showSpecHint && (
        <Alert
          className="tw-mb-4"
          type="info"
          showIcon
          message={t('products:detail.scn.specHint.title')}
          description={t('products:detail.scn.specHint.body')}
          action={
            <Button
              size="small"
              icon={<RightOutlined />}
              iconPosition="end"
              onClick={() => navigate(`/auth/products/scn/${product.scn}`)}
            >
              {t('products:detail.scn.drawer.viewSpecMaster')}
            </Button>
          }
        />
      )}

      <Typography.Title level={5}>{t('products:detail.scn.groupMembers')}</Typography.Title>
      <TableContainer>
        <Table<Product>
          rowKey="id"
          size="small"
          loading={membersLoading}
          dataSource={members}
          columns={memberColumns}
          pagination={false}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/products/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: t('products:detail.scn.noOtherMembers') }}
        />
      </TableContainer>
    </div>
  );
};

export default ScnSection;
